<template>
    <v-dialog v-model="editDialog.show" max-width="450px" @click:outside="$emit('close')">
        <!-- delete dialog -->
        <v-card id="projectEditDialog">
            <v-card-title>
                <span class="mr-2" style="text-transform: uppercase; font-weight: 100; font-size: 16px">Project Settings</span>
                <v-btn @click="$emit('close')" right top absolute icon color="primary"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-card-subtitle>
                <v-chip class="mt-1" small outlined color="primary"> {{ editDialog.item.title }}</v-chip>
            </v-card-subtitle>
            <v-card-text class="mt-2">
                <div class="qgreycard my-0 ml-auto">
                    <span class="lbl"
                        >Title
                        <v-tooltip max-width="250" color="" top>
                            <template v-slot:activator="{ on }">
                                <v-icon v-on="on" class="pl-0 mr-auto tooltipicon" small color="grey lightn-2">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>Select the title of your monitoring project!</span>
                        </v-tooltip>
                    </span>
                    <v-text-field
                        @keyup.enter="saveProject"
                        @input="editDialog.error = null"
                        dense
                        prepend-icon="mdi-label-outline"
                        color="primary"
                        hide-details
                        rounded
                        filled
                        class="my-auto mt-2"
                        v-model="editDialog.item.title"></v-text-field>
                </div>
                <div class="qgreycard my-0 ml-auto mt-5">
                    <span class="lbl"
                        >Domain
                        <v-tooltip max-width="250" color="" top>
                            <template v-slot:activator="{ on }">
                                <v-icon v-on="on" class="pl-0 mr-auto tooltipicon" small color="grey lightn-2">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>Select the focus domain of your monitoring project!</span>
                        </v-tooltip>
                    </span>
                    <v-text-field
                        prepend-icon="mdi-web"
                        @keyup.enter="saveProject"
                        @input="editDialog.error = null"
                        dense
                        color="primary"
                        hide-details
                        rounded
                        filled
                        class="my-auto mt-2"
                        v-model="inputDomain"></v-text-field>
                </div>
                <div class="qgreycard my-0 ml-auto mt-5">
                    <span class="lbl"
                        >Update Interval
                        <v-tooltip max-width="250" color="" top>
                            <template v-slot:activator="{ on }">
                                <v-icon v-on="on" class="pl-0 mr-auto tooltipicon" small color="grey lightn-2">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>How often the rankings of your keywords are updated. Changing the interval changes the credits reserved for this project.</span>
                        </v-tooltip>
                    </span>
                    <v-chip-group class="py-0 mt-1" color="primary" column v-model="intervalIndex" @change="editDialog.error = null">
                        <v-chip v-for="(option, i) in intervalOptions" :key="i" small filter filter-icon="mdi-check-circle">
                            <span style="text-transform: capitalize"> {{ option.text }}</span>
                        </v-chip>
                    </v-chip-group>
                    <div v-if="intervalChanged && !editDialog.item.paused && !editDialog.item.demo_project" class="mt-2" style="font-size: 12px">
                        <v-icon x-small class="mr-1">mdi-database-outline</v-icon>
                        Credit usage changes from <b>{{ usageBefore.total }}</b> to <b>{{ usageAfter.total }}</b> {{ usageAfter.unit }} per month.
                    </div>
                    <div v-if="intervalChanged && editDialog.item.paused" class="mt-2" style="font-size: 12px">
                        <v-icon x-small class="mr-1">mdi-pause-circle-outline</v-icon>
                        This project is paused. Credits for the new interval are reserved when you resume it.
                    </div>
                </div>
                <div class="d-flex">
                    <v-chip class="my-2 mx-auto py-1" style="white-space: normal; height: auto" v-if="editDialog.error" color="error"
                        ><v-icon class="mr-1">mdi-alert</v-icon>{{ editDialog.error }}</v-chip
                    >
                </div>
            </v-card-text>
            <v-card-actions class="px-auto">
                <v-btn class="mr-3" text rounded @click="$emit('close')"> Cancel </v-btn>
                <v-btn rounded class="ml-auto" color="primary" :loading="saving" @click="saveProject"> save </v-btn>
            </v-card-actions>
        </v-card>
        <CreditChangeConfirmDialog
            v-model="confirmDialog"
            :project="editDialog.item"
            title="Change Update Interval"
            :changes="['Update interval: ' + intervalOptionText(editDialog.item.interval) + ' to ' + intervalOptionText(selectedInterval)]"
            :before="usageBefore.total"
            :after="usageAfter.total"
            :unit="usageAfter.unit"
            :release-date="releaseDate"
            :loading="saving"
            @confirm="onConfirmIntervalChange" />
    </v-dialog>
</template>

<script>
    import {
        INTERVAL_OPTIONS,
        intervalOptionIndex,
        intervalLabel,
        projectCreditUsage,
        currentCycleEnd,
        buildNotEnoughCreditsMessage,
        importInProgressMessage,
        fetchEntryCount,
        withEntryCount
    } from "../helper/projectStatus"
    import CreditChangeConfirmDialog from "../dialogs/CreditChangeConfirmDialog.vue"

    export default {
        components: { CreditChangeConfirmDialog },
        props: { editDialog: Object },
        data() {
            return {
                inputDomain: "",
                // Feldinhalt beim Öffnen: nur eine geänderte Domain wird geprüft (siehe saveProject)
                initialInputDomain: "",
                intervalOptions: INTERVAL_OPTIONS,
                intervalIndex: intervalOptionIndex(this.editDialog.item.interval),
                confirmDialog: false,
                // Intervalländerung wurde im Bestätigungsdialog bestätigt -> saveProject speichert direkt
                intervalConfirmed: false,
                saving: false,
                // aktuelle Keyword-Zahl vom Server (siehe mounted), null bis geladen
                entryCount: null
            }
        },
        computed: {
            // Ende der laufenden Periode unter dem bisherigen Intervall: dann wird die alte Reservierung frei
            releaseDate() {
                return currentCycleEnd(this.editDialog.item, this.editDialog.item.interval)
            },
            // nur eine echte Änderung zählt: "monthly" bei einem Altprojekt mit 30 Tagen bleibt unverändert
            selectedInterval() {
                const option = this.intervalOptions[this.intervalIndex]
                return option ? option.value : null
            },
            intervalChanged() {
                return this.selectedInterval !== null && this.intervalIndex !== intervalOptionIndex(this.editDialog.item.interval)
            },
            usageBefore() {
                return projectCreditUsage(withEntryCount(this.editDialog.item, this.entryCount))
            },
            usageAfter() {
                return projectCreditUsage(withEntryCount(this.editDialog.item, this.entryCount), this.selectedInterval)
            },
            domainChanged() {
                return String(this.inputDomain || "").trim() !== String(this.initialInputDomain || "").trim()
            }
        },
        watch: {
            // inputDomain(val) {
            //     const domainRegex = /^((https?|ftp|smtp):\/\/)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}(:[0-9]+)?(\/\S*)?$/
            //     console.log(val)
            //     if (domainRegex.test(val)) {
            //         // Die eingegebene Domain ist gültig
            //         // Hier können Sie weitere Aktionen ausführen, wenn die Eingabe gültig ist
            //         console.log("Gültige Domain: " + val)
            //         try {
            //             let testUrl = new URL(val)
            //             console.log(testUrl.host)
            //             console.log(testUrl.hostname)
            //             this.editDialog.item.domain = testUrl.host
            //         } catch (error) {
            //             console.log(error)
            //         }
            //     } else {
            //         // Die eingegebene Domain ist ungültig
            //         // Hier können Sie weitere Aktionen ausführen, wenn die Eingabe ungültig ist
            //         console.log("Ungültige Domain")
            //     }
            // }
        },
        methods: {
            // Dieselbe Regel wie beim Anlegen (monitoring/setup/CreateProject.vue inputDomain-Watcher): mit http/https/ftp/smtp
            // URL.host (IDN als xn--, Standardport entfaellt), ohne Schema alles vor dem ersten "/" (Port bleibt); der Hostname
            // muss dem Muster des Anlegeformulars entsprechen. Gespeichert wird klein geschrieben: die monitoring-api vergleicht
            // die Projektdomain exakt mit den SERP-Domains - Leerzeichen, "_", @/?/#, rohes Unicode oder Grossbuchstaben matchen nie.
            validateDomain(val) {
                const rawDomain = String(val || "").trim()
                if (!rawDomain) return null
                const hasAnyScheme = /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(rawDomain)
                const hasAllowedScheme = /^(https?|ftp|smtp):\/\//i.test(rawDomain)
                if (hasAnyScheme && !hasAllowedScheme) return null

                try {
                    const testUrl = hasAllowedScheme ? new URL(rawDomain) : null
                    const hostValue = hasAllowedScheme ? testUrl.host : rawDomain.split("/")[0]
                    const hostname = hasAllowedScheme ? testUrl.hostname : hostValue.split(":")[0]
                    const hostnamePattern = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9-]{2,63}$/i
                    if (!hostnamePattern.test(hostname)) return null
                    return hostValue.toLowerCase()
                } catch (error) {
                    return null
                }
            },
            async saveProject() {
                if (this.editDialog.item.title.length < 3) {
                    this.editDialog.error = "Please provide a proper project title."
                    return
                }
                // Nur eine geaenderte Domain pruefen: ein Altprojekt, dessen gespeicherte Domain die heutige Regel ablehnt,
                // laesst sich sonst nicht mehr umbenennen oder im Intervall aendern. Unveraendert bleibt die gespeicherte Domain.
                if (this.domainChanged) {
                    let domain = this.validateDomain(this.inputDomain)
                    if (!domain) {
                        this.editDialog.error = "Please provide a proper project domain."
                        return
                    }
                    this.editDialog.item.domain = domain
                }

                // Intervalländerung verändert die Credit-Reservierung -> vorher bestätigen lassen
                if (this.intervalChanged && !this.intervalConfirmed) {
                    this.editDialog.error = null
                    this.confirmDialog = true
                    return
                }
                this.intervalConfirmed = false
                this.saving = true
                try {
                    await this.submitProject()
                } finally {
                    this.saving = false
                    this.confirmDialog = false
                }
            },
            async onConfirmIntervalChange() {
                this.intervalConfirmed = true
                await this.saveProject()
            },
            intervalOptionText(interval) {
                return intervalLabel(interval)
            },
            async submitProject() {
                const url =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/project/update/information"
                let post_data = {
                    company_id: localStorage.company_id,
                    project_id: this.editDialog.item._id,
                    fieldsToUpdate: { title: this.editDialog.item.title, domain: this.editDialog.item.domain }
                }
                // Intervall nur mitschicken, wenn es sich wirklich ändert - die API bucht dann Credits um
                // und lehnt die GANZE Änderung ab, falls sie nicht reichen.
                if (this.intervalChanged) post_data.fieldsToUpdate.interval = this.selectedInterval
                let response_data
                try {
                    response_data = await this.$helpers.axiosPost(url, post_data)
                } catch (error) {
                    console.log(error)
                    // 409: Intervalländerung während eines Imports gesperrt (siehe API /project/update/information)
                    const body = error && error.response && error.response.data
                    this.editDialog.error =
                        body && body.error === "import_in_progress" ? importInProgressMessage(body.import_type) : "Something went wrong. Please try again or contact the support!"
                    return
                }
                if (response_data.data && response_data.data.error == "not_enough_credits") {
                    this.editDialog.error = buildNotEnoughCreditsMessage({
                        requested: this.usageAfter.total - this.usageBefore.total,
                        unit: this.usageAfter.unit,
                        responseData: response_data.data
                    })
                    return
                }
                if (this.intervalChanged) this.editDialog.item.interval = this.selectedInterval
                this.$emit("saved")
            },
            // Projektliste bzw. offene Projektansicht kennen neu hochgeladene Keywords noch nicht
            async loadEntryCount() {
                this.entryCount = await fetchEntryCount({
                    projectId: this.editDialog.item._id,
                    axiosPost: this.$helpers.axiosPost.bind(this.$helpers),
                    cryptoJS: this.CryptoJS
                })
            }
        },
        mounted() {
            console.log(this.editDialog.item.domain)
            // Anzeige wie bisher als URL; eine Altdomain, aus der keine URL wird (z. B. mit Leerzeichen), steht unveraendert im Feld
            let initial = String(this.editDialog.item.domain || "")
            try {
                initial = String(new URL("https://" + this.editDialog.item.domain))
            } catch (error) {
                console.log(error)
            }
            this.initialInputDomain = initial
            this.inputDomain = initial
            this.loadEntryCount()
        }
    }
</script>
<style>
    #projectEditDialog .lbl {
        text-transform: uppercase;
        margin-bottom: 10px;
    }
</style>
